/**
 * Hook for Dashboard Ring Settings
 *
 * Provides access to team-specific dashboard ring configuration.
 * Falls back to defaults if not configured.
 */

import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useActiveTeamId } from '@/contexts/TeamContext';
import {
  DEFAULT_DASHBOARD_RING_SETTINGS,
  type DashboardRingSettingsType,
} from '@/features/settings/settings.schema';

/**
 * Query keys for dashboard ring settings
 */
export const dashboardRingSettingsKeys = {
  all: ['dashboardRingSettings'] as const,
  byTeam: (teamId: string) => [...dashboardRingSettingsKeys.all, teamId] as const,
};

/**
 * Fetch dashboard ring settings from Firestore
 */
async function fetchDashboardRingSettings(teamId: string): Promise<DashboardRingSettingsType> {
  const settingsRef = doc(db, 'teams', teamId, 'settings', 'appSettings');
  const snapshot = await getDoc(settingsRef);

  if (!snapshot.exists()) {
    return DEFAULT_DASHBOARD_RING_SETTINGS;
  }

  const data = snapshot.data();
  const dashboardRings = data?.dashboardRings;

  if (!dashboardRings) {
    return DEFAULT_DASHBOARD_RING_SETTINGS;
  }

  // Merge with defaults to ensure all fields are present
  // Also ensure businessDays is always an array
  const businessDays = Array.isArray(dashboardRings.businessDays)
    ? dashboardRings.businessDays
    : DEFAULT_DASHBOARD_RING_SETTINGS.businessDays;

  return {
    staleVehicleDays:
      dashboardRings.staleVehicleDays ?? DEFAULT_DASHBOARD_RING_SETTINGS.staleVehicleDays,
    criticalVehicleDays:
      dashboardRings.criticalVehicleDays ?? DEFAULT_DASHBOARD_RING_SETTINGS.criticalVehicleDays,
    idealCollectionDays:
      dashboardRings.idealCollectionDays ?? DEFAULT_DASHBOARD_RING_SETTINGS.idealCollectionDays,
    quoteFollowUpDays:
      dashboardRings.quoteFollowUpDays ?? DEFAULT_DASHBOARD_RING_SETTINGS.quoteFollowUpDays,
    businessDays,
  };
}

/**
 * Hook to get dashboard ring settings for the current team
 * Returns defaults if team context is not yet available (non-throwing)
 */
export function useDashboardRingSettings() {
  const teamId = useActiveTeamId(); // Non-throwing, returns undefined if not ready

  const query = useQuery({
    queryKey: dashboardRingSettingsKeys.byTeam(teamId || 'none'),
    queryFn: () => fetchDashboardRingSettings(teamId!),
    enabled: !!teamId, // Only run when teamId is available
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  // Return defaults while waiting for team context
  if (!teamId) {
    return {
      data: DEFAULT_DASHBOARD_RING_SETTINGS,
      isLoading: true,
      error: null,
      isError: false,
    };
  }

  return query;
}

/**
 * Check if a given date is a business day
 */
export function isBusinessDay(date: Date, businessDays: number[]): boolean {
  // getDay() returns 0=Sunday, 1=Monday, etc.
  // Our businessDays uses 1=Monday, ..., 7=Sunday
  const dayOfWeek = date.getDay();
  const normalizedDay = dayOfWeek === 0 ? 7 : dayOfWeek; // Convert Sunday from 0 to 7
  return businessDays.includes(normalizedDay);
}

/**
 * Get the next business day from a given date
 */
export function getNextBusinessDay(date: Date, businessDays: number[]): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + 1);

  // Keep incrementing until we find a business day (max 7 iterations)
  for (let i = 0; i < 7; i++) {
    if (isBusinessDay(result, businessDays)) {
      return result;
    }
    result.setDate(result.getDate() + 1);
  }

  // Fallback: return next day if no business days configured
  return new Date(date.getTime() + 24 * 60 * 60 * 1000);
}

/**
 * Count business days between two dates (exclusive of end date)
 */
export function countBusinessDays(startDate: Date, endDate: Date, businessDays: number[]): number {
  let count = 0;
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  while (current < end) {
    if (isBusinessDay(current, businessDays)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
}

/**
 * Add business days to a date
 */
export function addBusinessDays(date: Date, days: number, businessDays: number[]): Date {
  const result = new Date(date);
  let remaining = days;

  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    if (isBusinessDay(result, businessDays)) {
      remaining--;
    }
  }

  return result;
}
